<template>
  <svg
    ref="svg"
    class="tgo"
    :width="width"
    :height="height"
    @mousedown.prevent="on_mousedown"
    @wheel.prevent.stop="on_wheel"
  >
    <rect class="tgo_bg" :x="x0" y="0" :width="plot_width" :height="axis_y" />
    <rect
      v-for="(r, i) in recording_rects"
      :key="'r' + i"
      :x="r.x"
      y="0"
      :width="r.w"
      :height="axis_y"
      :fill="r.color"
      :fill-opacity="BAND_OPACITY"
    >
      <title>{{ r.title }}</title>
    </rect>
    <path
      v-for="p in paths"
      :key="p.id"
      class="tgo_line"
      :d="p.d"
      :stroke="p.color"
    />

    <template v-if="view">
      <rect class="tgo_view" :x="view.x" y="0.5" :width="view.w" :height="axis_y - 1" />
      <template v-for="edge in (view.w >= HANDLE_WIDTH * 3 ? ['from', 'to'] : [])" :key="edge">
        <rect
          class="tgo_grip"
          :x="(edge === 'from' ? view.x : view.x + view.w) - 1.5"
          :y="axis_y * 0.3"
          width="3"
          :height="axis_y * 0.4"
        />
        <rect
          class="tgo_handle"
          :x="(edge === 'from' ? view.x : view.x + view.w) - HANDLE_WIDTH / 2"
          y="0"
          :width="HANDLE_WIDTH"
          :height="axis_y"
          @mousedown.prevent.stop="on_handle_mousedown(edge)"
        />
      </template>
    </template>

    <g class="tgo_axis">
      <line :x1="x0" :x2="x1" :y1="axis_y" :y2="axis_y" />
      <template v-for="t in ticks" :key="t.x">
        <line :x1="t.x" :x2="t.x" :y1="axis_y" :y2="axis_y + 3" />
        <text
          v-if="t.label"
          :x="t.x"
          :y="height - 3"
          text-anchor="middle"
          :font-weight="t.bold ? 'bold' : null"
        >{{ t.label }}</text>
      </template>
      <text
        ref="from_label"
        class="tgo_end"
        :x="end_layout.from.x"
        :y="height - 3"
        :text-anchor="end_layout.from.anchor"
      >{{ span_labels.from }}</text>
      <text
        ref="to_label"
        class="tgo_end"
        :x="end_layout.to.x"
        :y="height - 3"
        :text-anchor="end_layout.to.anchor"
      >{{ span_labels.to }}</text>
    </g>

    <g v-if="show_legend" class="tgo_legend">
      <g
        v-for="(l, i) in legend"
        :key="l.color"
        :transform="`translate(${x1 + 8}, ${4 + i * LEGEND_ROW})`"
      >
        <rect width="8" height="8" :fill="l.color" :fill-opacity="BAND_OPACITY * 2" :stroke="l.color" />
        <text x="12" y="8">{{ l.text }}</text>
      </g>
    </g>
  </svg>
</template>

<script>
import { toRaw } from "vue";
import * as d3 from "d3";
import { date_axis_ticks } from "@/recordings/graphrender";

const Y_PAD = 4;
const MIN_VIEW_WIDTH = 4;
const HANDLE_WIDTH = 8;
const MIN_VIEW_MS = 60000;
const WHEEL_ZOOM_RATE = 0.0018;
const BAND_OPACITY = 0.18;
const AXIS_HEIGHT = 17;
const LEGEND_ROW = 12;
const END_LABEL_GAP = 6;
const CHAR_WIDTH = 6.5;
const DAY_MS = 86400000;

export default {
  name: "TimeGraphOverview",
  props: {
    series: { type: Array, default: () => [] },
    hidden_series: { type: Array, default: () => [] },
    recordings: { type: Array, default: () => [] },
    data_version: { type: Number, default: 0 },
    from: { type: Number, required: true },
    to: { type: Number, required: true },
    view_from: { type: Number, default: null },
    view_to: { type: Number, default: null },
    width: { type: Number, required: true },
    x0: { type: Number, required: true },
    x1: { type: Number, required: true },
    height: { type: Number, default: 72 },
    show_legend: { type: Boolean, default: true },
  },
  emits: ["pan", "zoom"],
  setup() {
    return { BAND_OPACITY, LEGEND_ROW, HANDLE_WIDTH };
  },
  data() {
    return {
      drag: null,
      pending_view: null,
      end_label_widths: null,
    };
  },
  watch: {
    view_from() { this.pending_view = null; },
    view_to() { this.pending_view = null; },
    span_labels: {
      immediate: true,
      handler() {
        this.$nextTick(() => this.measure_end_labels());
      },
    },
  },
  computed: {
    plot_width() {
      return Math.max(this.x1 - this.x0, 0);
    },
    ms_per_px() {
      return this.plot_width > 0 ? (this.to - this.from) / this.plot_width : 0;
    },
    axis_y() {
      return this.height - AXIS_HEIGHT;
    },
    paths() {
      // eslint-disable-next-line no-unused-expressions
      this.data_version;
      const span = this.to - this.from;
      if (!(span > 0) || this.plot_width <= 0) return [];
      const hidden = new Set(this.hidden_series);
      const series = toRaw(this.series).filter(s => !hidden.has(s.id) && s.data?.length);
      const value_of = d => {
        if (d.avgVal != null) return d.avgVal;
        if (d.minVal != null && d.maxVal != null) return (d.minVal + d.maxVal) / 2;
        return d.minVal ?? d.maxVal;
      };
      const time_of = d => (d.minDate ?? d.date)?.getTime?.();

      // Same-unit series share a scale so they stay comparable.
      const ranges = new Map();
      for (const s of series) {
        const r = ranges.get(s.unit) ?? { min: Infinity, max: -Infinity };
        for (const d of s.data) {
          const t = time_of(d);
          if (t == null || t < this.from || t > this.to) continue;
          const v = value_of(d);
          if (v == null || !Number.isFinite(v)) continue;
          if (v < r.min) r.min = v;
          if (v > r.max) r.max = v;
        }
        ranges.set(s.unit, r);
      }

      const top = Y_PAD;
      const bottom = this.axis_y - Y_PAD;
      const band = bottom - top;
      const result = [];
      for (const s of series) {
        const r = ranges.get(s.unit);
        if (!Number.isFinite(r.min)) continue;
        const range = r.max - r.min;
        let d_str = "";
        let pen_down = false;
        for (const d of s.data) {
          const t = time_of(d);
          const v = value_of(d);
          if (t == null || v == null || !Number.isFinite(v)) {
            pen_down = false; // null marker: gap in the data
            continue;
          }
          if (t < this.from || t > this.to) continue;
          const x = this.x0 + ((t - this.from) / span) * this.plot_width;
          const y = range > 0
            ? bottom - ((v - r.min) / range) * band
            : (top + bottom) / 2;
          d_str += `${pen_down ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`;
          pen_down = true;
        }
        if (d_str) result.push({ id: s.id, color: s.color, d: d_str });
      }
      return result;
    },

    recording_rects() {
      if (this.ms_per_px <= 0) return [];
      const now = Date.now();
      const recs = toRaw(this.recordings)
        .map(r => ({
          start: new Date(r.start).getTime(),
          stop: r.stop != null ? new Date(r.stop).getTime() : now,
          type: r.dto?.type,
        }))
        .filter(r => Number.isFinite(r.start) && r.stop >= this.from && r.start <= this.to)
        .sort((a, b) => a.start - b.start);

      const rects = [];
      for (const r of recs) {
        const a = this.x0 + (Math.max(r.start, this.from) - this.from) / this.ms_per_px;
        let b = this.x0 + (Math.min(r.stop, this.to) - this.from) / this.ms_per_px;
        if (b - a < 1) b = a + 1;
        const color = this.$camur.color_code_status_of_rectype(r.type);
        const last = rects[rects.length - 1];
        if (last && last.color === color && a <= last.x + last.w + 0.5) {
          last.w = Math.max(last.w, b - last.x);
          last.count++;
          continue;
        }
        rects.push({ x: a, w: b - a, color, type: r.type, count: 1 });
      }
      for (const r of rects) {
        const text = this.$camur.get_rectype_text(r.type);
        r.title = r.count > 1 ? `${text} (${r.count})` : text;
      }
      return rects;
    },

    legend() {
      const by_color = new Map();
      for (const r of this.recording_rects) {
        const text = this.$camur.get_rectype_text(r.type);
        const texts = by_color.get(r.color) ?? new Set();
        texts.add(text);
        by_color.set(r.color, texts);
      }
      return [...by_color].map(([color, texts]) => ({ color, text: [...texts].join(" / ") }));
    },

    end_layout() {
      const widths = this.end_label_widths ?? {
        from: this.span_labels.from.length * CHAR_WIDTH,
        to: this.span_labels.to.length * CHAR_WIDTH,
      };
      const from_x = Math.max(this.x0, widths.from / 2);
      const to_x = Math.min(this.x1, this.width - widths.to / 2);
      return {
        from: { x: from_x, anchor: "middle", inner: from_x + widths.from / 2 },
        to: { x: to_x, anchor: "middle", inner: to_x - widths.to / 2 },
      };
    },

    ticks() {
      if (this.ms_per_px <= 0) return [];
      const scale = d3.scaleTime().domain([this.from, this.to]).range([this.x0, this.x1]);
      const span = this.to - this.from;
      const count = Math.max(2, Math.floor(this.plot_width / 110));
      const left_limit = this.end_layout.from.inner + END_LABEL_GAP;
      const right_limit = this.end_layout.to.inner - END_LABEL_GAP;

      let values, step;
      if (span >= 8 * DAY_MS) {
        ({ ticks: values, step_ms: step } = date_axis_ticks(this.from, this.to, count));
      } else {
        values = scale.ticks(count);
        step = values.length > 1 ? values[1] - values[0] : span;
      }
      let previous = new Date(this.from);
      return values.map(t => {
        const x = scale(t);
        const show_year = t.getFullYear() !== previous.getFullYear();
        const show_month = show_year || t.getMonth() !== previous.getMonth();
        previous = t;
        const midnight = t.getHours() === 0 && t.getMinutes() === 0 && t.getSeconds() === 0;
        const shows_date = span >= 8 * DAY_MS || midnight;
        const bold = (show_year && step < 365 * DAY_MS) ||
          (show_month && step < 28 * DAY_MS) ||
          (shows_date && step < DAY_MS);
        const label = this.format_tick(t, span, show_year, show_month);
        const half = (label.length * CHAR_WIDTH) / 2;
        const fits = x - half >= left_limit && x + half <= right_limit;
        return { x, label: fits ? label : null, bold };
      });
    },
    span_labels() {
      const long = this.to - this.from >= 8 * DAY_MS;
      const opts = { show_date: true, show_time: !long };
      return {
        from: window.app.langutil.print_date_time(new Date(this.from), opts),
        to: window.app.langutil.print_date_time(new Date(this.to), opts),
      };
    },
    view() {
      if (this.view_from == null || this.view_to == null || this.ms_per_px <= 0) return null;
      const to_x = t => this.x0 + (t - this.from) / this.ms_per_px;
      let a = Math.min(Math.max(to_x(this.view_from), this.x0), this.x1);
      let b = Math.min(Math.max(to_x(this.view_to), this.x0), this.x1);
      if (b - a < MIN_VIEW_WIDTH) {
        const mid = (a + b) / 2;
        a = Math.max(this.x0, Math.min(mid - MIN_VIEW_WIDTH / 2, this.x1 - MIN_VIEW_WIDTH));
        b = a + MIN_VIEW_WIDTH;
      }
      return { x: a, w: b - a };
    },
  },
  methods: {
    format_tick(t, span, show_year, show_month) {
      const pdt = opts => window.app.langutil.print_date_time(t, opts);
      if (span >= 8 * DAY_MS) return pdt({ show_date: true, show_year, show_month, show_time: false });
      const midnight = t.getHours() === 0 && t.getMinutes() === 0 && t.getSeconds() === 0;
      if (midnight) return pdt({ show_date: true, show_year, show_time: false });
      return pdt({ show_date: false, show_time: true });
    },
    measure_end_labels() {
      const from = this.$refs.from_label?.getComputedTextLength?.();
      const to = this.$refs.to_label?.getComputedTextLength?.();
      if (!(from > 0) || !(to > 0)) return;
      const w = this.end_label_widths;
      if (w && Math.abs(w.from - from) < 0.5 && Math.abs(w.to - to) < 0.5) return;
      this.end_label_widths = { from, to };
    },
    local_x(event) {
      return event.clientX - this.$refs.svg.getBoundingClientRect().left;
    },
    time_at(x) {
      return this.from + (x - this.x0) * this.ms_per_px;
    },

    on_wheel(event) {
      if (this.ms_per_px <= 0 || this.view_from == null || this.view_to == null) return;
      const current = this.pending_view ?? { from: this.view_from, to: this.view_to };
      const anchor = Math.min(Math.max(this.time_at(this.local_x(event)), current.from), current.to);
      const duration = current.to - current.from;
      let factor = Math.exp(WHEEL_ZOOM_RATE * event.deltaY);
      const max_duration = this.to - this.from;
      if (duration * factor > max_duration) factor = max_duration / duration;
      if (duration * factor < MIN_VIEW_MS) factor = MIN_VIEW_MS / duration;
      let from = anchor - (anchor - current.from) * factor;
      let to = anchor + (current.to - anchor) * factor;

      if (to - from >= max_duration) {
        from = this.from;
        to = this.to;
      }
      this.pending_view = { from, to };
      this.$emit("zoom", { from, to });
    },
    on_handle_mousedown(edge) {
      if (this.view_from == null || this.view_to == null) return;
      this.drag = { edge, from: this.view_from, to: this.view_to };
      window.addEventListener("mousemove", this.on_mousemove);
      window.addEventListener("mouseup", this.on_mouseup);
    },
    on_mousedown(event) {
      if (this.ms_per_px <= 0 || this.view_from == null || this.view_to == null) return;
      const x = this.local_x(event);
      const view = this.view;
      const inside_view = view && x >= view.x && x <= view.x + view.w;

      let center = (this.view_from + this.view_to) / 2;
      if (!inside_view) {
        center = this.from + (x - this.x0) * this.ms_per_px;
        this.$emit("pan", center);
      }
      this.drag = { start_x: x, center };
      window.addEventListener("mousemove", this.on_mousemove);
      window.addEventListener("mouseup", this.on_mouseup);
    },
    on_mousemove(event) {
      if (!this.drag) return;
      if (this.drag.edge) {
        const t = this.time_at(this.local_x(event));
        let { from, to } = this.drag;
        if (this.drag.edge === "from") from = Math.min(t, to - MIN_VIEW_MS);
        else to = Math.max(t, from + MIN_VIEW_MS);
        this.$emit("zoom", { from, to });
        return;
      }
      const dx = this.local_x(event) - this.drag.start_x;
      this.$emit("pan", this.drag.center + dx * this.ms_per_px);
    },
    on_mouseup() {
      this.drag = null;
      window.removeEventListener("mousemove", this.on_mousemove);
      window.removeEventListener("mouseup", this.on_mouseup);
    },
  },
  beforeUnmount() {
    this.on_mouseup();
  },
};
</script>

<style scoped>
.tgo {
  display: block;
  cursor: pointer;
  user-select: none;
}
.tgo_bg {
  fill: #fff;
  stroke: rgba(0, 0, 0, 0.15);
  stroke-width: 1;
}
.tgo_line {
  fill: none;
  stroke-width: 1;
  opacity: 0.85;
}
.tgo_view {
  fill: rgba(0, 0, 0, 0.12);
  stroke: rgba(0, 0, 0, 0.35);
  stroke-width: 1;
  cursor: grab;
}
.tgo_grip {
  fill: rgba(0, 0, 0, 0.45);
  pointer-events: none;
}
.tgo_handle {
  fill: transparent;
  cursor: ew-resize;
}
.tgo_axis line {
  stroke: rgba(0, 0, 0, 0.35);
  stroke-width: 1;
}
.tgo_axis text {
  font-size: 11px;
  fill: #555;
}
.tgo_axis .tgo_end {
  font-weight: 600;
  fill: #333;
}
.tgo_legend text {
  font-size: 11px;
  fill: #444;
}
</style>
